import { fg } from '@atlaskit/platform-feature-flags';
import {
	confluenceCQLQuery,
	type EntityATI,
	fetchJiraFieldsQuery,
	generateSearchPageCacheKey,
	type ResultsSort,
	searchPageQuery,
	searchPageQueryCache,
	type SearchPageQueryVariables,
	SORT_FIELD_KEY,
} from '@atlassian/search-client';
import type { SearchExperimentLayer } from '@atlassian/search-experiment';

import {
	type PrimaryProductKey,
	PrimaryProductKeys,
	ProductKeys,
} from '../../../common/constants/products';
import { type SelectedFiltersType } from '../../../common/constants/types';
import { EmptyEntitiesError } from '../../../common/utils/fetch-error';
import { getEmptySelectedFilters } from '../../../common/utils/filters';

const MULTISITE_SUPPORTED_PRODUCTS_WITH_CONTRIBUTORS_FILTER: ProductKeys[] = [
	ProductKeys.Confluence,
	ProductKeys.Jira,
	ProductKeys.JiraServiceManagement,
	ProductKeys.JiraProductDiscovery,
];

/**
 * Determines the appropriate cloud ARI for search based on product support and filters.
 * Search aggregator does not support multiple sites search with contributors filter for all products, loom or bitbucket.
 * Therefore, this function handles the fallback logic for unsupported cases, where single site search is used instead.
 *
 * TODO: https://hello.jira.atlassian.cloud/browse/ALLOY-1010 - long term fix in aggregator and remove this fallback
 */
export const determineCloudARI = ({
	cloudId,
	selectedFilters,
	selectedProduct,
	linkedCloudARIs,
	sourceProduct,
}: {
	cloudId: string;
	selectedFilters: SelectedFiltersType | null;
	selectedProduct?: ProductKeys;
	linkedCloudARIs?: string[];
	sourceProduct?: PrimaryProductKey;
}): string | string[] => {
	const isProductSupportedForMultiSiteWithContributorsFilter = selectedProduct
		? MULTISITE_SUPPORTED_PRODUCTS_WITH_CONTRIBUTORS_FILTER.includes(selectedProduct)
		: false;

	const contributorsFilter = selectedFilters?.commonFilters.contributorsFilter;
	const hasContributorsFilter = Array.isArray(contributorsFilter) && contributorsFilter.length > 0;

	const hasLinkedCloudARIs = Array.isArray(linkedCloudARIs) && linkedCloudARIs.length > 0;

	// Rovo being rolled out for townsquare, mercury
	// and all of them using confluence ARI, we keep using confluence ARI for them.
	// However, for jira, we use jira ARI as there was a known issue when using confluence ARI, it applied Confluence ip restrictions to Jira.
	// Leave linkedCloudARIs as is, as it is used for multi-site search, and comes from config data.
	const fallbackCloudARI =
		sourceProduct === PrimaryProductKeys.Jira && fg('jira_site_ari_query_enabled')
			? `ari:cloud:jira::site/${cloudId}`
			: `ari:cloud:confluence::site/${cloudId}`;

	// Fall back to single site search when using contributors filter with unsupported products
	if (!isProductSupportedForMultiSiteWithContributorsFilter && hasContributorsFilter) {
		return fallbackCloudARI;
	}

	// Use multi-site search with linked cloud ARIs if available
	if (hasLinkedCloudARIs) {
		return linkedCloudARIs;
	}

	return fallbackCloudARI;
};

export type FetchResultsProps = {
	cql?: string;
	query: string;
	originalQuery?: string;
	entities: EntityATI[];
	selectedFilters: SelectedFiltersType | null;
	cloudId: string;
	aggAbsoluteUrl?: string | undefined;
	searchSessionId: string;
	sessionId?: string;
	sort: ResultsSort;
	sourceProduct?: PrimaryProductKey;
	experimentId?: string;
	shadowExperimentId?: string;
	experimentLayers?: SearchExperimentLayer[];
	after?: string;
	limit: number;
	enableHighlighting?: boolean;
	skip3pEntityHydration?: boolean;
	includeSpaceSubtype?: boolean;
	linkedCloudARIs?: string[];
	selectedProduct?: ProductKeys;
	fanoutExperimentCohort?: string;
};

export const fetchResults = async ({
	cql,
	query,
	originalQuery,
	entities,
	selectedFilters,
	cloudId,
	aggAbsoluteUrl,
	searchSessionId,
	sessionId,
	sort,
	sourceProduct,
	experimentId,
	shadowExperimentId,
	experimentLayers,
	after,
	limit,
	enableHighlighting,
	skip3pEntityHydration,
	fanoutExperimentCohort,
	includeSpaceSubtype = false,
	linkedCloudARIs,
	selectedProduct,
}: FetchResultsProps) => {
	if (cql) {
		const response = confluenceCQLQuery(
			{
				cql,
				first: limit,
				after,
			},
			cloudId,
		);

		return response;
	}

	const {
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
	} = selectedFilters || getEmptySelectedFilters();

	const sortVariables = sort.field === SORT_FIELD_KEY.RELEVANCE ? [] : [sort];

	// If the original query param is present, we want to disable query replacement
	const disableQueryReplacement = !!originalQuery;

	// Determine the appropriate cloud ARI based on filters and linked cloudARIs
	const cloudIdARI = determineCloudARI({
		cloudId,
		selectedFilters,
		selectedProduct,
		linkedCloudARIs,
		sourceProduct,
	});

	const variables = {
		query,
		queryVersion: 1,
		cloudIdARI,
		tenantId: cloudId,
		limit,
		commonFilters,
		confluenceFilters,
		jiraFilters,
		mercuryFilters,
		compassFilters,
		thirdPartyFilters,
		assetsFilters,
		entities,
		searchSessionId,
		sessionId,
		sort: sortVariables,
		sourceProduct,
		experimentId,
		shadowExperimentId,
		experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
			name,
			layerId,
			shadowId,
			definitions,
		})),
		after: after,
		disableQueryReplacement,
		enableHighlighting,
		skip3pEntityHydration,
		includeSocialSignals: true,
		fanoutExperimentCohort,
		includeSpaceSubtype: includeSpaceSubtype,
		includeIssueTypeAvatarUrl: true,
		includeAssigneeField: fg('rovo_search_assignee_field_fps'),
		includeBoardObject: fg('rovo_search_update_board_url'),
	} satisfies SearchPageQueryVariables;

	if (entities.length === 0) {
		throw new EmptyEntitiesError();
	}

	const response = searchPageQuery({
		variables,
		aggAbsoluteUrl,
		originalQuery,
	});

	// TODO: QS-6145 maybe here is a good place to merge results into cache store as HTTP response parts come in ?
	// const deferredResponse = deferredSearchPageQuery({ variables, aggAbsoluteUrl, originalQuery });

	// If the original query param is not present, we want to cache the response with the replacement query
	if (!originalQuery && fg('rovo-search-spelling-correction')) {
		const value = await response;
		const queryInfo = value.data?.search?.results?.queryInfo;
		if (
			queryInfo &&
			queryInfo.replacementQuery &&
			queryInfo.originalQuery &&
			queryInfo.replacementQuery !== queryInfo.originalQuery
		) {
			searchPageQueryCache.set(
				generateSearchPageCacheKey(
					{ ...variables, query: queryInfo.replacementQuery },
					queryInfo.originalQuery,
				),
				response,
			);
		}
	}

	return response;
};

export const fetchJiraFields = async ({
	issueKeys,
	cloudId,
	aggAbsoluteUrl,
}: {
	issueKeys: string[];
	cloudId: string;
	aggAbsoluteUrl?: string | undefined;
}) => {
	const variables = {
		issueKeys,
		cloudId,
	};

	const response = fetchJiraFieldsQuery({
		variables,
		aggAbsoluteUrl,
	});

	return response;
};
